import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { usePhoto } from "./photo.js";
import { Moai, type Expression } from "./Moai.js";
import { desinscrirePorteur, inscrirePorteur, rejouerAnnonce, terminer, useCouvre, useMessager, vibrerPour, type Message } from "./messager.js";
import type { SkinId } from "./skins.js";
import { ESPACES_HUMAINS } from "../espaces.js";
import { useVoix } from "./voix.js";

/* ------------------------------------------------------------------ */
/* Cadre                                                               */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Trois appuis sur la tête de Hoa                                     */
/* ------------------------------------------------------------------ */

/*
 * La porte des salles ouvertes par la régie : trois appuis rapprochés sur
 * n'importe quel avatar de Hoa. Rien ne l'annonce à l'écran — ces salles se
 * rejoignent d'ordinaire par leur QR code — mais l'équipe d'un événement
 * doit pouvoir y aller sans en scanner un par salle.
 */
let tripleHoa: (() => void) | null = null;
let appuis: number[] = [];

export function ecouterTripleHoa(f: (() => void) | null): void {
  tripleHoa = f;
}

let rejouer: number | null = null;

function surAppui(e: { target: EventTarget | null }): void {
  const el = e.target as Element | null;
  if (!el || typeof el.closest !== "function" || !el.closest(".hoa-moai")) return;
  const t = Date.now();
  appuis = [...appuis.filter((x) => t - x < 1200), t];
  if (rejouer !== null) window.clearTimeout(rejouer);
  rejouer = null;
  if (appuis.length >= 3) {
    appuis = [];
    tripleHoa?.();
    return;
  }
  // Un seul appui sur Hoa (pas le début d'un triple) : la dernière annonce.
  if (appuis.length === 1 && el.closest(".hoa-etape.courante, .hoa-message-secours")) {
    rejouer = window.setTimeout(() => {
      rejouer = null;
      rejouerAnnonce();
    }, 450);
  }
}

/** Le texte posé sur une couleur : noir sur une couleur claire, blanc sinon. */
export function surCouleur(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  const l = 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
  return l > 0.36 ? "#111111" : "#ffffff";
}

export function Ecran(props: { skin: SkinId; children: ReactNode; dessus?: ReactNode; label?: string }): JSX.Element {
  // Espaces Humains : les boutons prennent la couleur de la marque du stand.
  const voix = useVoix();
  const marque = ESPACES_HUMAINS && voix?.couleur ? voix.couleur : null;
  const style = marque ? ({ "--accent": marque, "--sur-accent": surCouleur(marque) } as CSSProperties) : undefined;
  return (
    <div className="hoa" data-skin={props.skin} data-marque={marque ? "" : undefined} style={style} onClick={surAppui}>
      <main className="hoa-ecran" aria-label={props.label}>
        {props.children}
        <MessageSecours />
        {props.dessus}
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ce que Hoa dit en passant (messager.ts)                             */
/* ------------------------------------------------------------------ */

type Phase = "repos" | "efface" | "ecrit" | "tient" | "retour" | "revient";

function mouvementReduit(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Le déroulé d'un message dans une bulle : le texte de l'étape s'en va, le
 * message s'écrit, tient le temps d'être lu, s'efface lettre à lettre, et le
 * texte de l'étape revient. Un appui pendant l'écriture la termine ; un
 * appui pendant la lecture passe à la suite.
 *
 * `avecDefaut` : la bulle a un texte à elle (la bulle de l'étape). Sans lui
 * (la bulle de secours), la bulle s'en va à la fin.
 */
function useDeroule(m: Message | null, suite: boolean, avecDefaut: boolean): { phase: Phase; texte: string; auteur: string | null; rebond: number; appuyer: () => void } {
  const [phase, setPhase] = useState<Phase>("repos");
  const [texte, setTexte] = useState("");
  const [auteur, setAuteur] = useState<string | null>(null);
  const [rebond, setRebond] = useState(0);
  const appui = useRef<() => void>(() => undefined);
  const suiteRef = useRef(suite);
  suiteRef.current = suite;
  /** La bulle est déjà vide : le message d'avant vient de s'effacer. */
  const vide = useRef(false);

  useEffect(() => {
    if (!m) {
      vide.current = false;
      setPhase("repos");
      setTexte("");
      setAuteur(null);
      return;
    }
    let annule = false;
    const minuteurs: number[] = [];
    const pause = (ms: number): Promise<void> =>
      new Promise((r) => {
        minuteurs.push(window.setTimeout(r, ms));
      });
    const reduit = mouvementReduit();
    let saute = false;
    let finLecture: (() => void) | null = null;
    appui.current = () => {
      if (finLecture) finLecture();
      else saute = true;
    };

    const ecrire = async (t: string): Promise<void> => {
      if (reduit) {
        setTexte(t);
        return;
      }
      // Une seconde et demie au plus, quelle que soit la longueur.
      const delai = Math.max(16, Math.min(34, 1500 / t.length));
      const pasDe = Math.max(1, Math.round((16 * t.length) / 1500));
      for (let k = 0; k < t.length; k += delai > 16 ? 1 : pasDe) {
        if (annule) return;
        if (saute) break;
        setTexte(t.slice(0, k + 1));
        await pause(delai);
      }
      setTexte(t);
    };
    const effacer = async (t: string): Promise<void> => {
      if (!reduit) {
        const pasDe = Math.max(1, Math.ceil(t.length / 22));
        for (let k = t.length; k > 0; k -= pasDe) {
          if (annule) return;
          setTexte(t.slice(0, k));
          await pause(16);
        }
      }
      setTexte("");
    };

    void (async () => {
      vibrerPour(m);
      setRebond((r) => r + 1);
      if (!vide.current && avecDefaut) {
        setPhase("efface");
        await pause(reduit ? 0 : 230);
        if (annule) return;
      }
      vide.current = false;
      setTexte("");
      setAuteur(m.auteur);
      setPhase("ecrit");
      await ecrire(m.texte);
      if (annule) return;
      setPhase("tient");
      await new Promise<void>((r) => {
        finLecture = r;
        minuteurs.push(window.setTimeout(r, m.tenir));
      });
      finLecture = null;
      if (annule) return;
      setPhase("retour");
      await effacer(m.texte);
      if (annule) return;
      setAuteur(null);
      if (suiteRef.current) {
        // Un autre message attend : il s'écrit dans la bulle déjà vide.
        vide.current = true;
        terminer(m.n);
        return;
      }
      setRebond((r) => r + 1);
      setPhase("revient");
      await pause(reduit ? 0 : 280);
      if (annule) return;
      setPhase("repos");
      terminer(m.n);
    })();

    return () => {
      annule = true;
      for (const t of minuteurs) window.clearTimeout(t);
      appui.current = () => undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m?.n]);

  return { phase, texte, auteur, rebond, appuyer: () => appui.current() };
}

function Tape(props: { phase: Phase; texte: string; auteur: string | null }): JSX.Element {
  return (
    <div className="hoa-bulle-tape" aria-hidden="true">
      {props.auteur ? <small className="hoa-voix-nom">{props.auteur}</small> : null}
      <span>{props.texte}</span>
      {props.phase === "ecrit" ? <i className="hoa-curseur" /> : null}
    </div>
  );
}

/** L'étape en cours qui porte Hoa : ses bulles peuvent dire les messages. */
const EtapePorteuse = createContext<number | null>(null);
let etapes = 0;

/**
 * La bulle de l'étape en cours : elle garde son texte, et prête sa place
 * aux messages le temps de les dire.
 */
function BulleVivante(props: { children: ReactNode; id?: string; etape: number }): JSX.Element {
  const [moi, setMoi] = useState<number | null>(null);
  useEffect(() => {
    const i = inscrirePorteur(props.etape);
    setMoi(i);
    return () => desinscrirePorteur(i);
  }, [props.etape]);
  const { courant, pilote, suite } = useMessager();
  const actif = moi !== null && typeof pilote === "object" && pilote !== null && pilote.porteur === moi;
  const d = useDeroule(actif ? courant : null, suite, true);
  const parle = d.phase !== "repos";
  const ecrit = d.phase === "ecrit" || d.phase === "tient" || d.phase === "retour";
  const classes = ["hoa-bulle", "vivante", `phase-${d.phase}`, d.rebond > 0 ? `rebond-${d.rebond % 2}` : ""].filter(Boolean).join(" ");
  return (
    <div className={classes} id={props.id} aria-live={parle ? undefined : "polite"} onClick={parle ? d.appuyer : undefined}>
      <div className="hoa-bulle-defaut" aria-hidden={ecrit || undefined}>
        {props.children}
      </div>
      {ecrit ? <Tape phase={d.phase} texte={d.texte} auteur={d.auteur} /> : null}
    </div>
  );
}

/**
 * Quand aucune bulle de Hoa n'est visible (Mon compte, la carte en grand,
 * une feuille ouverte), le message passe dans une petite bulle en haut de
 * l'écran, sans croix, qui s'en va seule. Et pour les lecteurs d'écran, le
 * message entier une seule fois, pas lettre à lettre.
 */
function MessageSecours(): JSX.Element {
  const { courant, pilote, suite } = useMessager();
  const d = useDeroule(pilote === "secours" ? courant : null, suite, false);
  return (
    <>
      <div className="hoa-sr" role="status">
        {courant ? `${courant.auteur ? `${courant.auteur} : ` : ""}${courant.texte}` : ""}
      </div>
      {d.phase !== "repos" ? (
        <div className={`hoa-message-secours phase-${d.phase}`} onClick={d.appuyer}>
          <Moai expression={courant?.expression ?? "surpris"} />
          <div className={`hoa-bulle${d.rebond > 0 ? ` rebond-${d.rebond % 2}` : ""}`}>
            <Tape phase={d.phase} texte={d.texte} auteur={d.auteur} />
          </div>
        </div>
      ) : null}
    </>
  );
}

export function Barre(props: { children: ReactNode }): JSX.Element {
  return <header className="hoa-barre">{props.children}</header>;
}

/* ------------------------------------------------------------------ */
/* « Moi » : le bouton rond en haut à droite, et son menu              */
/* ------------------------------------------------------------------ */

export function Silhouette(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </svg>
  );
}

/** La fleur des points : cinq pétales, un cœur. */
export function Fleur(props: { taille?: number }): JSX.Element {
  const t = props.taille ?? 22;
  const petales = [0, 72, 144, 216, 288];
  return (
    <svg width={t} height={t} viewBox="0 0 24 24" aria-hidden="true" className="hoa-fleur">
      {petales.map((a) => (
        <ellipse key={a} cx="12" cy="6.4" rx="4" ry="5.2" fill="currentColor" transform={`rotate(${a} 12 12)`} />
      ))}
      <circle cx="12" cy="12" r="3.3" fill="var(--fond)" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    </svg>
  );
}

/** Les points de l'appareil, à gauche du bouton « Moi ». */
export function PastillePoints(props: { n: number; onClick: () => void }): JSX.Element {
  // Espaces Humains n'a pas de points.
  if (ESPACES_HUMAINS) return <></>;
  return (
    <button type="button" className="hoa-points-pastille" aria-label={`${props.n} points`} onClick={props.onClick}>
      <Fleur />
      <span>{props.n}</span>
    </button>
  );
}

/** Le gain d'une rencontre : « +10 » avec la fleur. */
export function Gain(props: { n: number }): JSX.Element {
  if (ESPACES_HUMAINS) return <></>;
  return (
    <span className="hoa-gain">
      <Fleur taille={20} />+{props.n} points
    </span>
  );
}

/** Le bouton « Moi » : la photo de profil, sinon l'initiale du pseudo, sinon une silhouette. */
export function BoutonMoi(props: { initiale: string; ouvert: boolean; onClick: () => void }): JSX.Element {
  const photo = usePhoto();
  return (
    <button
      type="button"
      className={photo ? "hoa-rond hoa-moi avec-photo" : "hoa-rond hoa-moi"}
      aria-label="Moi"
      aria-haspopup="menu"
      aria-expanded={props.ouvert}
      onClick={props.onClick}
    >
      {photo ? <img src={photo} alt="" /> : props.initiale || <Silhouette />}
    </button>
  );
}

/** Une photo de profil ronde, ou l'initiale du pseudo. */
export function Avatar(props: { src?: string | null; nom: string; taille?: number }): JSX.Element {
  const t = props.taille ?? 28;
  return (
    <span className="hoa-avatar" style={{ width: t, height: t, fontSize: Math.max(13, Math.round(t * 0.45)) }} aria-hidden="true">
      {props.src ? <img src={props.src} alt="" /> : (props.nom.trim()[0] ?? "?").toUpperCase()}
    </span>
  );
}

export interface EntreeMenu {
  libelle: string;
  onClick: () => void;
}

/** Le menu de « Moi » : posé sous le bouton, fermé par un appui ailleurs ou Échap. */
export function MenuMoi(props: { entrees: EntreeMenu[]; onFermer: () => void }): JSX.Element {
  const premier = useRef<HTMLButtonElement>(null);
  const fermer = useRef(props.onFermer);
  fermer.current = props.onFermer;
  useEffect(() => {
    premier.current?.focus();
    const touche = (e: KeyboardEvent): void => {
      if (e.key === "Escape") fermer.current();
    };
    addEventListener("keydown", touche);
    return () => removeEventListener("keydown", touche);
  }, []);
  return (
    <div className="hoa-voile clair" onClick={props.onFermer}>
      <div className="hoa-menu" role="menu" aria-label="Moi" onClick={(e) => e.stopPropagation()}>
        {props.entrees.map((e, i) => (
          <button key={e.libelle} ref={i === 0 ? premier : undefined} type="button" role="menuitem" onClick={e.onClick}>
            {e.libelle}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Une feuille qui monte du bas, l'écran d'avant grisé derrière. */
export function Feuille(props: { label: string; children: ReactNode; onFermer?: () => void }): JSX.Element {
  const fermer = useRef(props.onFermer);
  fermer.current = props.onFermer;
  useEffect(() => {
    const touche = (e: KeyboardEvent): void => {
      if (e.key === "Escape") fermer.current?.();
    };
    addEventListener("keydown", touche);
    return () => removeEventListener("keydown", touche);
  }, []);
  useCouvre();
  return (
    <div className="hoa-voile" onClick={props.onFermer}>
      <div className="hoa-feuille" role="dialog" aria-modal="true" aria-label={props.label} onClick={(e) => e.stopPropagation()}>
        <span className="hoa-poignee" aria-hidden="true" />
        <EtapePorteuse.Provider value={null}>{props.children}</EtapePorteuse.Provider>
      </div>
    </div>
  );
}

/**
 * Une feuille qui monte du bas et qu'on règle en hauteur par sa seule
 * poignée : glisser la poignée vers le haut l'agrandit, vers le bas la
 * réduit. Le contenu défile à l'intérieur ; le reste de la feuille ne bouge
 * pas sous le doigt, pour ne pas confondre défiler et redimensionner.
 */
export function FeuilleReglable(props: { label: string; entete?: ReactNode; children: ReactNode; onFermer: () => void }): JSX.Element {
  const haut = (): number => (typeof window === "undefined" ? 800 : window.innerHeight);
  const borne = (x: number): number => Math.round(Math.max(260, Math.min(haut() - 48, x)));
  const [h, setH] = useState(() => borne(haut() * 0.62));
  const debut = useRef<{ y: number; h: number } | null>(null);
  useCouvre();
  const fermer = useRef(props.onFermer);
  fermer.current = props.onFermer;
  useEffect(() => {
    const touche = (e: KeyboardEvent): void => {
      if (e.key === "Escape") fermer.current();
    };
    addEventListener("keydown", touche);
    return () => removeEventListener("keydown", touche);
  }, []);
  return (
    <div className="hoa-voile" onClick={props.onFermer}>
      <div className="hoa-feuille reglable" role="dialog" aria-modal="true" aria-label={props.label} style={{ height: h }} onClick={(e) => e.stopPropagation()}>
        <div
          className="hoa-poignee-zone"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Agrandir ou réduire"
          aria-valuemin={260}
          aria-valuemax={haut() - 48}
          aria-valuenow={h}
          tabIndex={0}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            debut.current = { y: e.clientY, h };
          }}
          onPointerMove={(e) => {
            const d = debut.current;
            if (d) setH(borne(d.h + (d.y - e.clientY)));
          }}
          onPointerUp={() => (debut.current = null)}
          onPointerCancel={() => (debut.current = null)}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") setH((x) => borne(x + 80));
            if (e.key === "ArrowDown") setH((x) => borne(x - 80));
          }}
        >
          <span className="hoa-poignee" />
        </div>
        <EtapePorteuse.Provider value={null}>
          {props.entete}
          <div className="hoa-feuille-defile">{props.children}</div>
        </EtapePorteuse.Provider>
      </div>
    </div>
  );
}

/** Hoa qui parle hors de la frise : l'avatar et sa bulle, côte à côte. */
export function Parole(props: { children: ReactNode; expression?: Expression }): JSX.Element {
  return (
    <div className="hoa-rang">
      <Moai expression={props.expression} />
      <Bulle>{props.children}</Bulle>
    </div>
  );
}

export function Bulle(props: { children: ReactNode; passee?: boolean; id?: string }): JSX.Element {
  const etape = useContext(EtapePorteuse);
  if (etape !== null && !props.passee) {
    return (
      <BulleVivante id={props.id} etape={etape}>
        {props.children}
      </BulleVivante>
    );
  }
  return (
    <div className={props.passee ? "hoa-bulle passee" : "hoa-bulle"} id={props.id} aria-live="polite">
      {props.children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Frise                                                               */
/* ------------------------------------------------------------------ */

export type EtatEtape = "faite" | "courante" | "avenir" | "barree";

/**
 * Une étape de la frise. L'étape courante porte Hoa ; les étapes faites
 * sont un point plein, celles à venir un cercle.
 */
export function Etape(props: {
  etat: EtatEtape;
  dernier?: boolean;
  libre?: boolean;
  children: ReactNode;
  label?: string;
  /** HOA libéré : le moaï saute de joie (tribu réunie). */
  anime?: boolean;
  /** L'expression de Hoa sur cette étape (neutre par défaut, joie si libre). */
  expression?: Expression;
}): JSX.Element {
  const courante = props.etat === "courante";
  const [id] = useState(() => ++etapes);
  const { courant, pilote } = useMessager();
  // Hoa dit un message dans une bulle de cette étape : il sursaute, il change
  // de tête — sauf libéré : la joie de la tribu réunie passe avant.
  const parle = courante && courant !== null && typeof pilote === "object" && pilote !== null && pilote.etape === id;
  return (
    <section
      className={`${courante ? "hoa-etape courante" : "hoa-etape"}${props.anime ? " hoa-libere" : ""}${parle ? " hoa-parle" : ""}`}
      aria-current={courante ? "step" : undefined}
      aria-label={props.label}
    >
      <div className="hoa-noeud" aria-hidden="true">
        {courante ? (
          <Moai libre={props.libre} expression={parle && courant && !props.libre ? courant.expression : props.expression} />
        ) : (
          <i className={props.etat === "faite" ? "fait" : props.etat === "barree" ? "barre" : ""} />
        )}
        {props.dernier ? null : <u />}
      </div>
      <div className="hoa-corps">
        <EtapePorteuse.Provider value={courante ? id : null}>{props.children}</EtapePorteuse.Provider>
      </div>
    </section>
  );
}

/**
 * Le texte d'une étape de la frise. Avec `onClick`, un appui sur le point
 * ouvre l'étape (la carte du rendez-vous, le chat) ou la referme.
 */
export function Libelle(props: { children: ReactNode; barre?: boolean; onClick?: () => void; ouvert?: boolean }): JSX.Element {
  if (props.onClick) {
    return (
      <button type="button" className={`libelle hoa-libelle-lien${props.ouvert ? " ouvert" : ""}`} aria-expanded={props.ouvert === true} onClick={props.onClick}>
        <span>{props.children}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    );
  }
  return <span className={props.barre ? "libelle barre" : "libelle"}>{props.children}</span>;
}

/* ------------------------------------------------------------------ */
/* Pictos                                                              */
/* ------------------------------------------------------------------ */

export function Croix(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function Retour(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export function Envoyer(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h13M13 6l6 6-6 6" />
    </svg>
  );
}


/* ------------------------------------------------------------------ */
/* Dates et mots                                                       */
/* ------------------------------------------------------------------ */

/** « 20 h », « 20 h 30 ». */
export function heure(ms: number): string {
  const d = new Date(ms);
  const h = d.getHours();
  const m = d.getMinutes();
  // Espaces insécables : « 15 h 16 » ne se coupe jamais en fin de ligne.
  return m === 0 ? `${h}\u00a0h` : `${h}\u00a0h\u00a0${String(m).padStart(2, "0")}`;
}

/** « à 21 h 30 » aujourd'hui ; « demain à 21 h 30 », « samedi à 21 h 30 » sinon. */
export function aHeure(ms: number, maintenant: number): string {
  const memeJour = new Date(ms).toDateString() === new Date(maintenant).toDateString();
  return memeJour ? `à ${heure(ms)}` : `${jour(ms, maintenant)} à ${heure(ms)}`;
}

/** « ce soir », « demain », « mardi ». */
export function jour(ms: number, maintenant: number): string {
  const a = new Date(maintenant);
  const b = new Date(ms);
  const j0 = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const j1 = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  const ecart = Math.round((j1 - j0) / 86_400_000);
  if (ecart <= 0) return b.getHours() >= 17 || a.getHours() >= 17 ? "ce soir" : "aujourd'hui";
  if (ecart === 1) return "demain";
  // À six jours et plus, le jour de la semaine seul prêterait à confusion.
  if (ecart >= 6) return b.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric" });
  return b.toLocaleDateString("fr-FR", { weekday: "long" });
}

/** « dans 12 min », « dans 1 h 05 ». */
export function dans(ms: number, maintenant: number): string {
  const min = Math.max(0, Math.ceil((ms - maintenant) / 60_000));
  if (min < 1) return "maintenant";
  if (min < 60) return `dans ${min}\u00a0min`;
  // Une tribu créée pour un autre jour : en jours, pas en dizaines d'heures.
  if (min >= 24 * 60) {
    const j = Math.floor(min / (24 * 60));
    return `dans ${j}\u00a0${j > 1 ? "jours" : "jour"}`;
  }
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `dans ${h}\u00a0h` : `dans ${h}\u00a0h\u00a0${String(r).padStart(2, "0")}`;
}

/** « Lina, Tom et toi ». */
export function etToi(noms: string[]): string {
  if (noms.length === 0) return "Toi";
  if (noms.length === 1) return `${noms[0]} et toi`;
  return `${noms.slice(0, -1).join(", ")}, ${noms[noms.length - 1]} et toi`;
}

export function pluriel(n: number, un: string, plusieurs: string): string {
  return n > 1 ? plusieurs : un;
}

/** Quatre kilomètres à l'heure : la marche d'un piéton qui ne se presse pas. */
export function minutesAPied(metres: number): number {
  return Math.max(1, Math.round(metres / 66.7));
}
